import { useEffect, useMemo, useState } from 'react'
import { Link as GatsbyLink, navigate, PageProps } from 'gatsby'
import { useAsyncEffect, useBoolean } from 'ahooks'
import clsx from 'clsx'
import moment from 'moment'
import Layout from 'components/layouts/tow-column'
import {
  Button,
  Card,
  Divider,
  Dropdown,
  Error,
  Icon,
  Menu,
  SEO,
  showMessage,
  Skeleton,
  Tag,
  Text,
  Title,
  Tooltip,
} from 'components/common'
import {
  ApprovalStatusCard,
  BookingInfoCard,
  BookingsEditReason,
  CancelConfirmationModal,
  CancelApprovalModal,
  CheckedStatusIcon,
  CheckedStatusType,
  CheckedStatusUpdateCard,
  CheckedUpdateType,
  LineHeader,
  LineItem,
  PaymentStatusIcon,
  PriceDisplayCard,
  PriceList,
  OrderCancelForm,
  reasonSelectSet,
} from 'components/bookings'
import {
  DiscountFeeInput,
  ExtensionFee,
  OrderApproval,
  OrderCancelReason,
  OrderErrorCode,
  OrderLineItem,
  OrderTimestampInput,
  useExportInvoiceMutation,
  useGeneralSettingQuery,
  useOrderApprovalUpdateMutation,
  useOrderCancelMutation,
  useOrderDisapprovalUpdateMutation,
  useOrderQuery,
  useOrderUpdateTimestampMutation,
  useOrderPendingUpdateMutation,
  useOrderUndoCancelMutation,
} from 'lib/graphql/storeadmin'
import { colors } from 'lib/colors'
import { bookingsErrorHandling } from 'lib/hooks'
import {
  date as stringDate,
  japaneseDateTimeFormat as jStringDate,
  time,
} from 'lib/str-formatter'
import * as styles from './styles.module.css'
import { SAGA_ARENA_PRODUCT_NAME, SAGA_ARENA_USAGE_TYPE } from 'config'
import { UndoCancelModal } from 'components/bookings/modals/UndoCancelModal'
import BookingCancelCard from 'components/bookings/cards/BookingCancelCard'

type BookingTab = { key: string; tab: string; index: number }

type LineItemForDisplay = {
  name: string
  baseTimeUnit: number
  image: string
  price: number
  specialPrice: number
  quantity: number
  id: string
  unit: string
  title: string
}

type SagaArenaLineItemForDisplay = {
  [date: string]: LineItemForDisplay[]
}

const onBack = () => navigate('../')

export const Head = () => <SEO title="予約詳細" />

const IdPage = ({ location, params: { id } }: PageProps): JSX.Element => {
  const { data, loading, error, refetch } = useOrderQuery({
    variables: { id },
    fetchPolicy: 'network-only',
  })

  const [onUpdateTimestamp, { loading: updateLoading }] =
    useOrderUpdateTimestampMutation()
  const [approve, { loading: approveLoading }] =
    useOrderApprovalUpdateMutation()
  const [disapprove, { loading: disapproveLoading }] =
    useOrderDisapprovalUpdateMutation()
  const [exportInvoice, { loading: exporting }] = useExportInvoiceMutation({
    variables: { id },
  })
  const { data: generalSettingData } = useGeneralSettingQuery()
  const [cancelOrder, { loading: cancelOrderLoading }] =
    useOrderCancelMutation()
  const [orderPendingUpdate, { loading: orderPendingUpdateLoading }] =
    useOrderPendingUpdateMutation()
  const [orderUndoCancel, { loading: orderUndoCancelLoading }] =
    useOrderUndoCancelMutation()
  const [tab, setTab] = useState<string>('')
  const [showCancelModal, showCancelModalAction] = useBoolean(false)
  const [showCancelApprovalModal, showCancelApprovalModalAction] =
    useBoolean(false)
  const [showUndoCancelModal, showUndoCancelModalAction] = useBoolean(false)
  const [newLineItemsByDate, setNewLineItemsByDate] =
    useState<SagaArenaLineItemForDisplay>({})
  const [newLineItems, setNewLineItems] = useState<LineItemForDisplay[]>()
  const [undoCancelReason, setUndoCancelReason] = useState('')

  const {
    approval,
    approvalRequired,
    bookingDates,
    customer,
    calendar,
    canceledAt,
    cancellationFee,
    cancelReason,
    contactOfRepresentative,
    createdAt,
    discountFee,
    entryFee,
    extensionFee,
    lineItems,
    numberOfPeople,
    orderNumber,
    payment,
    period,
    purpose,
    reasonForDisapproval,
    saleOfGoods,
    originalSubTotalPrice,
    originalSubTotalPriceTaxExcluded,
    originalTotalTax,
    originalTotalPrice,
    subTotalPrice,
    subTotalPriceTaxExcluded,
    totalTax,
    totalPrice,
    discountPrice,
    specialPriceIncluded,
    modifierInfo,
    updatedAt,
    estimateNumber,
  } = data?.order || {}
  // CardTab で選ばれている日付が data.order.bookingDates の何番目か
  const dateIndex = useMemo<number>(() => {
    return data?.order?.bookingDates?.findIndex((date) => date.date === tab)
  }, [data, tab])

  // CardTab で選択された日付でのチェックインステータス
  const checkedStatus = useMemo<CheckedStatusType>(() => {
    if (data?.order?.bookingDates?.[dateIndex]?.checkedInAt) {
      if (data?.order?.bookingDates?.[dateIndex]?.checkedOutAt) {
        return 'checkedOut'
      } else {
        return 'checkedIn'
      }
    } else return 'beforeChecked'
  }, [data, dateIndex])

  useAsyncEffect(async () => {
    await refetch()
  }, [location, refetch])

  useEffect(() => {
    if (data?.order?.bookingDates?.[0]?.date) {
      if (!tab) {
        setTab(data?.order?.bookingDates?.[0]?.date)
        if (isSagaArena) {
          const newLineItems: OrderLineItem[] = lineItems?.edges?.map(
            (edges) => edges.lineItem
          )
          setNewLineItemsByDate(sagaArenaGroupByDate(newLineItems))
        } else {
          const newLineItems: LineItemForDisplay[] = lineItems?.edges?.map(
            ({ lineItem }) => {
              return {
                id: lineItem.id,
                name: lineItem.name.jaJp,
                baseTimeUnit: lineItem.baseTimeUnit,
                image: lineItem.product.images[0]?.src ?? null,
                price: lineItem.price,
                specialPrice: lineItem.specialPrice,
                quantity: lineItem.quantity,
                unit: lineItem.unit,
                title: lineItem.variantTitle.jaJp,
              }
            }
          )
          setNewLineItems(newLineItems)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, tab])

  const priceList: PriceList = {
    originalSubTotalPrice: originalSubTotalPrice,
    originalSubTotalPriceTaxExcluded: originalSubTotalPriceTaxExcluded,
    originalTotalTax: originalTotalTax,
    originalTotalPrice: originalTotalPrice,
    subTotal: subTotalPrice,
    subTotalPriceTaxExcluded: subTotalPriceTaxExcluded,
    totalTax: totalTax,
    totalPrice: totalPrice,
    specialPriceIncluded: specialPriceIncluded,
    discountPrice: discountPrice,
  }

  // extensionFeeの型を合わせる
  const extensionFeeAddId = extensionFee
    ? extensionFee.map((item) => ({
        ...item,
        id: null,
      }))
    : [
        {
          id: null,
          reason: '',
          value: 0,
        },
      ]

  const extensionData: ExtensionFee[] = extensionFeeAddId

  const discountData: DiscountFeeInput = discountFee || {
    reason: '',
    value: 0,
  }

  // CardTab に表示される日付
  const bookingTabs: BookingTab[] = bookingDates?.map(({ date }, index) => ({
    key: date,
    tab: stringDate(date),
    index: index,
  }))

  const bookingDate = useMemo(() => {
    return bookingDates?.[dateIndex]
  }, [dateIndex, bookingDates])

  const finalLoading =
    loading || updateLoading || approveLoading || disapproveLoading

  const displayTag = useMemo(() => {
    const tagList: JSX.Element[] = []
    if (canceledAt) {
      tagList.push(
        <Tag
          key="canceled"
          color={colors.YAMABUKI}
          icon={<Icon.MinusCircleOutlined />}
        >
          キャンセル済
        </Tag>
      )
    } else if (finalLoading === false) {
      if (
        bookingDates?.[dateIndex]?.checkedInAt &&
        bookingDates?.[dateIndex]?.checkedOutAt
      ) {
        tagList.push(
          <Tag
            key="CheckedOut"
            color={colors.NAE}
            icon={<Icon.CheckCircleOutlined />}
          >
            チェックアウト済
          </Tag>
        )
      } else if (bookingDates?.[dateIndex]?.checkedInAt) {
        tagList.push(
          <Tag
            key="CheckedIn"
            color={colors.MIZUASAGI}
            icon={<Icon.CheckCircleOutlined />}
          >
            チェックイン済
          </Tag>
        )
      } else {
        tagList.push(
          <Tag
            key="BeforeCheck"
            color={colors.KUCHINASHI}
            icon={<Icon.InfoCircleOutlined />}
          >
            チェックイン前
          </Tag>
        )
      }
    }

    if (approval === OrderApproval.Disapproved) {
      tagList.push(
        <Tag
          key="unapproved"
          color={colors.KURENAI}
          icon={<Icon.CloseCircleOutlined />}
        >
          非承認
        </Tag>
      )
    } else if (approval === OrderApproval.Approved) {
      tagList.push(
        <Tag
          key="approved"
          color={colors.NAE}
          icon={<Icon.CheckCircleOutlined />}
        >
          承認済
        </Tag>
      )
    } else if (approval === OrderApproval.Pending) {
      tagList.push(
        <Tag
          key="pending"
          color={colors.YAMABUKI}
          icon={<Icon.MinusCircleOutlined />}
        >
          承認待ち
        </Tag>
      )
    }
    return tagList
  }, [approval, bookingDates, canceledAt, dateIndex, finalLoading])

  // チェックイン状況での表示分岐
  const statusIcon = (): JSX.Element => {
    if (bookingDates?.[dateIndex]?.checkedInAt) {
      if (bookingDates?.[dateIndex]?.checkedOutAt) {
        return <CheckedStatusIcon status="CheckedOut" />
      }
      return <CheckedStatusIcon status="CheckedIn" />
    }
    return <CheckedStatusIcon status="BeforeCheck" />
  }

  const onClickInvoice = async () => {
    try {
      const {
        data: { reportInvoiceExport },
        errors,
      } = await exportInvoice()
      const invoiceError = reportInvoiceExport?.userErrors
      if (invoiceError?.length > 0 || errors?.length > 0) {
        showMessage('error', '利用明細出力に失敗しました。')
      } else {
        const url = reportInvoiceExport.url
        return window.open(url, '_blank')
      }
    } catch (e) {
      showMessage('error', '利用明細出力時に予期せぬエラーが発生しました。')
    }
  }

  // チェクイン/チェックアウト/ステータスクリアを行う Mutation 実行
  const onCheckedInOut = async (
    time: moment.Moment,
    type: CheckedUpdateType
  ) => {
    const input: OrderTimestampInput = {
      orderId: id,
      timeStamps: [
        {
          date: tab,
        },
      ],
    }
    try {
      if (type === 'チェックイン') {
        input.timeStamps[0].checkedInAt = time.format('HH:mm:ss')
      } else if (type === 'チェックアウト') {
        input.timeStamps[0].checkedInAt = bookingDates[dateIndex].checkedInAt
        input.timeStamps[0].checkedOutAt = time.format('HH:mm:ss')
      }

      const { data, errors } = await onUpdateTimestamp({
        variables: { input: input },
      })
      const timestampErrors = data?.orderUpdateTimestamp?.orderUserErrors
      if (timestampErrors?.length > 0 || errors?.length > 0) {
        timestampErrors?.forEach(({ code }) => {
          switch (code) {
            case OrderErrorCode.InvalidCheckinTime:
              return showMessage(
                'error',
                'チェックイン時間はチェックアウト時間より前の時間でなければなりません。'
              )

            case OrderErrorCode.InvalidCheckoutTime:
              return showMessage(
                'error',
                'チェックアウト時間はチェックイン時間より後の時間でなければなりません。'
              )

            case OrderErrorCode.FutureDate:
            default:
              return showMessage(
                'error',
                '現在日時より後の日時の予約はチェックインできません。'
              )
          }
        })
      } else {
        await refetch()
      }
    } catch (e) {
      showMessage(
        'error',
        'チェックインステータスの更新時に予期せぬエラーが発生しました。'
      )
    }
  }

  const onApprove = async (): Promise<void> => {
    try {
      const { data, errors } = await approve({ variables: { id: id } })

      const approveErrors = data?.orderApprovalUpdate?.orderUserErrors
      if (approveErrors?.length > 0 || errors?.length > 0) {
        showMessage('error', '予約の承認ができませんでした。')
      } else {
        showMessage('success', '予約を承認しました。')
        await refetch()
      }
    } catch (e) {
      showMessage('error', '予約の承認時に予期せぬエラーが発生しました。')
    }
  }

  const onDisapprove = async (reason: string): Promise<void> => {
    try {
      const { data, errors } = await disapprove({
        variables: { id: id, reasonForDisapproval: reason },
      })
      const disapprovalErrors = data?.orderDisapprovalUpdate?.orderUserErrors
      if (disapprovalErrors?.length > 0 || errors?.length > 0) {
        showMessage('error', '予約の非承認に失敗しました。')
      } else {
        showMessage('success', '予約を非承認しました。')
        await refetch()
      }
    } catch (e) {
      showMessage('error', '予約の非承認時に予期せぬエラーが発生しました。')
    }
  }

  const onOrderCancelModalOk = async (
    values: OrderCancelForm
  ): Promise<void> => {
    const { cancelReason, cancellationFee } = values
    try {
      const { data, errors } = await cancelOrder({
        variables: {
          id,
          cancellationFee,
          cancelReason: OrderCancelReason[cancelReason],
        },
      })
      const orderCancelError = data?.orderCancel?.orderUserErrors
      if (orderCancelError.length > 0 || errors?.length > 0) {
        const orderCancelErrorDescriptions =
          bookingsErrorHandling(orderCancelError)
        if (
          orderCancelErrorDescriptions[0] ===
          'メール送信時にエラーが発生しました。'
        ) {
          showMessage(
            'success',
            'メール送信時にエラーが発生しましたが、予約をキャンセルしました。'
          )
          await refetch()
        } else {
          showMessage('error', '予約をキャンセルすることができませんでした。')
        }
      } else {
        showMessage('success', '予約をキャンセルしました。')
        await refetch()
      }
    } catch (e) {
      showMessage('error', '予約のキャンセル時に予期せぬエラーが発生しました。')
    }
  }

  const onApprovalCancelModalOk = async (): Promise<void> => {
    try {
      const { data, errors } = await orderPendingUpdate({
        variables: {
          id: id,
        },
      })
      const orderPendingUpdateError = data?.orderPendingUpdate?.orderUserErrors
      if (orderPendingUpdateError.length > 0 || errors?.length > 0) {
        const orderPendingUpdateErrorDescriptions = bookingsErrorHandling(
          orderPendingUpdateError
        )
        if (
          orderPendingUpdateErrorDescriptions[0] ===
          'メール送信時にエラーが発生しました。'
        ) {
          showMessage(
            'success',
            'メール送信時にエラーが発生しましたが、承認済みをキャンセルしました。'
          )
          await refetch()
        } else {
          showMessage(
            'error',
            '承認済みをキャンセルすることができませんでした。'
          )
        }
      } else {
        showMessage('success', '承認済みをキャンセルしました。')
        await refetch()
      }
    } catch (e) {
      showMessage(
        'error',
        '承認済みのキャンセル時に予期せぬエラーが発生しました。'
      )
    }
  }

  const onUndoCancelModalOk = async (reason: string): Promise<void> => {
    try {
      const { data, errors } = await orderUndoCancel({
        variables: {
          undoCancelReason: reason,
          id: id,
        },
      })
      const orderUndoCancelError = data?.orderUndoCancel?.orderUserErrors
      if (orderUndoCancelError.length > 0 || errors?.length > 0) {
        const orderUndoCancelErrorDescriptions =
          bookingsErrorHandling(orderUndoCancelError)
        if (
          orderUndoCancelErrorDescriptions[0] ===
          'メール送信時にエラーが発生しました。'
        ) {
          showMessage(
            'success',
            'メール送信時にエラーが発生しましたが、予約のキャンセルを取り消しました。'
          )
          await refetch()
        } else {
          if (
            orderUndoCancelError.some((error) => error.code === 'UNAVAILABLE')
          ) {
            showMessage(
              'error',
              '既に同じ日時で予約が作成されているため、予約のキャンセルを取り消すことができませんでした。'
            )
          } else {
            showMessage(
              'error',
              '予約のキャンセルを取り消すことができませんでした。'
            )
          }
        }
      } else {
        showMessage('success', '予約のキャンセルを取り消しました。')
        await refetch()
      }
    } catch (e) {
      showMessage(
        'error',
        '予約のキャンセル取り消し時に、予期せぬエラーが発生しました。'
      )
    }
  }

  // canceledAt に値が入っている場合はキャンセルされた予約なので、disabled = true にする
  const disabled = typeof canceledAt === 'string'

  const dateTimeFormatForTab = `${jStringDate(tab)?.date} ${time(
    period?.start
  )} - ${time(period?.end)}`

  const isSagaArena = useMemo(() => {
    if (loading) return
    return generalSettingData?.generalSetting?.domain === 'saga-arena'
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  const isMainArena = useMemo(() => {
    if (!lineItems) return
    return lineItems?.edges?.some(({ lineItem }) =>
      lineItem?.name?.jaJp.includes(SAGA_ARENA_PRODUCT_NAME.MAIN_ARENA)
    )
  }, [lineItems])

  // 延長、設営・撤去がバリエーション名に含まれているかをチェックする関数
  const checkUsageTypeIncluded = (title: string) => {
    const usageTypeList = [
      SAGA_ARENA_USAGE_TYPE.SETUP_TEARDOWN,
      SAGA_ARENA_USAGE_TYPE.EXTENSION,
    ]
    return usageTypeList.some((type) => title.includes(type))
  }

  const sagaArenaGroupByDate = (
    data: OrderLineItem[]
  ): SagaArenaLineItemForDisplay => {
    // もし SagaArena でなければ null を返す
    if (!isSagaArena) return null
    const result = {}
    // 各 lineItem を処理する
    data.forEach((lineItem) => {
      // lineItem の各プロパティを展開する
      const {
        name,
        variantTitle,
        product,
        baseTimeUnit,
        unit,
        price,
        specialPrice,
        arenaBookingInfo,
      } = lineItem
      // arenaBookingInfo が存在しなければスキップする
      if (!arenaBookingInfo) return
      // 各 arenaBookingInfo を処理する
      arenaBookingInfo.forEach((info) => {
        if (info) {
          const dateOfUse = info.dateOfUse
          // もし dateOfUse が結果に存在しなければ追加する
          if (!result[dateOfUse]) {
            result[dateOfUse] = []
          }
          // 各 lineItem の関連データを結果の dateOfUse に追加する
          result[dateOfUse].push({
            name: name.jaJp,
            baseTimeUnit: checkUsageTypeIncluded(variantTitle.jaJp)
              ? 60
              : baseTimeUnit,
            image: product.images[0]?.src ?? null,
            price: price,
            specialPrice: specialPrice,
            quantity: info.quantity,
            id: info.id,
            unit: unit,
            title: variantTitle.jaJp,
          })
        }
      })
    })
    return result
  }

  // カードのタブに表示する日付をフィルタリングする
  const getFilteredTab = (
    bookingTabs: BookingTab[],
    lineItems: OrderLineItem[]
  ): BookingTab[] => {
    let matchingDates: string[] = []
    lineItems?.forEach((lineItem) => {
      lineItem?.arenaBookingInfo
        ?.filter(({ quantity }) => quantity > 0)
        .forEach((info) => {
          if (bookingTabs.some((tab) => tab.key === info?.dateOfUse)) {
            matchingDates.push(info?.dateOfUse)
          }
        })
    })

    // 重複を取り除く
    matchingDates = Array.from(new Set(matchingDates))

    // 日付順に並び替える
    matchingDates.sort((a, b) => (a > b ? 1 : -1))

    const res: BookingTab[] = matchingDates.map((date, index) => ({
      key: date,
      tab: stringDate(date),
      index,
    }))

    return res
  }

  if (error) {
    return Error.render(error)
  }

  return (
    <>
      <Layout
        title={`#${orderNumber}`}
        tags={displayTag}
        actions={
          <>
            <Button type="text" loading={exporting} onClick={onClickInvoice}>
              利用明細
            </Button>
            <Button type="text" disabled={!!canceledAt}>
              <GatsbyLink to="./edit">
                <Tooltip>編集</Tooltip>
              </GatsbyLink>
            </Button>
            <Dropdown
              overlay={
                <>
                  <Menu className={clsx('bg-white')}>
                    <Menu.Item
                      disabled={disabled}
                      onClick={showCancelModalAction.setTrue}
                    >
                      予約をキャンセルする
                    </Menu.Item>
                  </Menu>
                  <Menu className={clsx('bg-white')}>
                    {disabled && (
                      <Menu.Item
                        // disabled={!disabled}
                        onClick={showUndoCancelModalAction.setTrue}
                      >
                        予約のキャンセルを取り消す
                      </Menu.Item>
                    )}
                  </Menu>
                  <Menu className={clsx('bg-white')}>
                    {approval === OrderApproval.Approved && (
                      <Menu.Item
                        disabled={disabled}
                        onClick={showCancelApprovalModalAction.setTrue}
                      >
                        承認済みをキャンセルする
                      </Menu.Item>
                    )}
                  </Menu>
                </>
              }
            >
              <Button type="text">
                その他の操作 <Icon.DownOutlined />
              </Button>
            </Dropdown>
          </>
        }
        loading={loading}
        onBack={onBack}
      >
        <Layout.Section size="large">
          {/* キャンセル料金 */}
          {cancellationFee > 0 && (
            <Layout.Item>
              <BookingCancelCard
                cancelReason={reasonSelectSet(cancelReason)}
                cancellationFee={cancellationFee}
              />
            </Layout.Item>
          )}

          {isMainArena && (
            <Layout.Item>
              <Card shadow>
                <Title level={5}>見積書情報</Title>
                <dl className="flex justify-between mb-4">
                  <dt>見積書番号</dt>
                  <dd className="mb-0">{estimateNumber ?? '登録なし'}</dd>
                </dl>
              </Card>
            </Layout.Item>
          )}
          <Layout.Item>
            <Card
              shadow
              loading={finalLoading}
              tabList={
                isSagaArena
                  ? getFilteredTab(
                      bookingTabs,
                      lineItems?.edges?.map((edge) => edge?.lineItem)
                    )
                  : bookingTabs
              }
              activeTabKey={tab}
              onTabChange={setTab}
            >
              <div className="flex items-center justify-between mb-10">
                {statusIcon()}
                <div className={clsx(styles.flex__auto__end)}>
                  <Text strong style={{ fontSize: '16px' }}>
                    {dateTimeFormatForTab || ''}
                  </Text>
                </div>
              </div>
              <LineHeader isBookingDetails isSagaArena={isSagaArena} />
              <Card.Divider style={{ marginTop: 0, marginBottom: '16px' }} />
              <div className="space-y-8">
                {(isSagaArena ? newLineItemsByDate[tab] : newLineItems)?.map(
                  (item, index) => (
                    <LineItem
                      date={tab}
                      image={item.image}
                      key={item.id + index}
                      title={item.name}
                      variantTitle={item.title}
                      price={item.price}
                      specialPrice={item.specialPrice}
                      baseTimeUnit={item.baseTimeUnit}
                      bookingDates={{
                        start: bookingDates[0].date,
                        end: bookingDates[bookingDates.length - 1].date,
                      }}
                      period={data?.order.period}
                      quantity={item.quantity}
                      isBookingDetails
                      isSagaArena={isSagaArena}
                    />
                  )
                )}
              </div>
            </Card>
          </Layout.Item>

          <Layout.Item>
            <PriceDisplayCard
              discountData={discountData}
              extensionData={extensionData}
              priceList={priceList}
              title={<PaymentStatusIcon status={payment?.status} />}
              customerPaidAmount={payment}
              totalPrice={totalPrice}
              loading={finalLoading}
              isCanceled={!!canceledAt}
            />
          </Layout.Item>

          <Layout.Item>
            {finalLoading ? (
              <Skeleton active />
            ) : (
              <>
                <div className={clsx(styles.flex__between, 'mr-5', 'ml-5')}>
                  <Text strong style={{ fontSize: '16px' }}>
                    タイムライン
                  </Text>
                </div>
                <Divider />
                <BookingsEditReason
                  bookingDate={bookingDate}
                  cancelReason={cancelReason}
                  createdAt={createdAt}
                  modifyReasons={modifierInfo}
                  reasonForDisapproval={reasonForDisapproval}
                  updatedAt={updatedAt}
                />
              </>
            )}
          </Layout.Item>
        </Layout.Section>

        <Layout.Section size="small">
          <Layout.Item>
            <CheckedStatusUpdateCard
              loading={finalLoading}
              bookingDate={bookingDate}
              disabled={disabled}
              approval={approval}
              checkedStatus={checkedStatus}
              date={moment(tab)}
              onCheckedUpdate={onCheckedInOut}
            />
          </Layout.Item>

          <Layout.Item>
            {/* 要承認かつ非承認処理が行われていない予約のみ操作可能 */}
            <ApprovalStatusCard
              loading={finalLoading}
              disabled={disabled}
              approval={approval}
              approvalRequired={approvalRequired}
              onApprove={onApprove}
              onDisapprove={onDisapprove}
            />
          </Layout.Item>

          <Layout.Item>
            <BookingInfoCard
              customer={{
                id: customer?.id,
                firstName: customer?.firstName,
                lastName: customer?.lastName,
                corporationName: customer?.corporationName,
                customerType: customer?.type,
                email: customer?.email,
                phone: customer?.phone,
              }}
              bookingDetail={{
                entryFee: entryFee,
                numberOfPeople: numberOfPeople,
                purpose: purpose,
                saleOfGoods: saleOfGoods,
                contactOfRepresentative: contactOfRepresentative,
                exceed:
                  extensionFee?.reduce((sum, item) => sum + item.value, 0) || 0,
                discount: discountFee?.value || 0,
              }}
              discountDetail={{
                discount: customer?.userMetaData?.discount,
                limit: customer?.userMetaData?.limit,
              }}
              usageScheduleDetails={{
                approval: calendar?.approval,
                organizer: calendar?.organizer,
                eventName: calendar?.eventName,
                publishableDate: calendar?.publishableDate,
              }}
              loading={finalLoading}
              disabled={disabled}
              editable={false}
              isArena={
                generalSettingData?.generalSetting.domain === 'saga-arena'
              }
            />
          </Layout.Item>
        </Layout.Section>
      </Layout>

      <CancelConfirmationModal
        visible={showCancelModal}
        onClose={showCancelModalAction.setFalse}
        onOk={onOrderCancelModalOk}
        loading={cancelOrderLoading}
        totalPrice={totalPrice}
      />
      <CancelApprovalModal
        visible={showCancelApprovalModal}
        onClose={showCancelApprovalModalAction.setFalse}
        onOk={onApprovalCancelModalOk}
        loading={orderPendingUpdateLoading}
      />
      <UndoCancelModal
        visible={showUndoCancelModal}
        onClose={showUndoCancelModalAction.setFalse}
        onOk={onUndoCancelModalOk}
        undoCancelReason={undoCancelReason}
        setUndoCancelReason={setUndoCancelReason}
        loading={orderUndoCancelLoading}
      />
    </>
  )
}

export default IdPage
